// hooks/useMemberData.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { getMemberConfig, MEMBER_TYPES } from '../utils/membersConfig';

const apiKey = process.env.REACT_APP_API_KEY;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to fetch member data - handles both congress and executive/judicial formats
 */
const useMemberData = (memberType = MEMBER_TYPES.HOUSE, limit = 1, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  const config = getMemberConfig(memberType);
  const cacheKey = `members_${memberType}_${limit}`;
  const timestampKey = `${cacheKey}_timestamp`;

  const fetchData = useCallback(async (skipCache = false) => {
    // Check cache first
    if (!skipCache) {
      const cachedData = localStorage.getItem(cacheKey);
      const cachedTimestamp = localStorage.getItem(timestampKey);

      if (cachedData && cachedTimestamp) {
        const age = Date.now() - parseInt(cachedTimestamp, 10);
        if (age < CACHE_DURATION) {
          try {
            const parsed = JSON.parse(cachedData);
            setData(parsed);
            setLoading(false);
            fetchData(true); // Background refresh
            return;
          } catch (e) {
            // Cache invalid
          }
        }
      }
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      if (!skipCache) setLoading(true);
      setError(null);

      let url;
      
      // Build URL based on member type
      if (config.dataType === 'executive') {
        // Presidents and Vice Presidents - use their specific endpoints
        url = `${config.apiEndpoint}?order_by=${config.orderBy || 'id'}&limit=${limit}&skip=0`;
      } else if (config.dataType === 'judicial') {
        // Justices endpoint
        url = `${config.apiEndpoint}?limit=${limit}&skip=0`;
      } else {
        // Congress endpoint (House/Senate) - fetch more to filter
        const fetchLimit = limit * 3;
        url = `${config.apiEndpoint}?limit=${fetchLimit}&skip=0`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`,
        },
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch members: ${response.status}`);
      }

      let membersData = await response.json();

      // Filter by chamber for Congress members
      if (config.dataType === 'congress' && config.filterField && config.filterValue) {
        membersData = membersData.filter((member) => {
          const latestTerm = member.terms?.[member.terms.length - 1];
          return latestTerm?.[config.filterField] === config.filterValue;
        });
        membersData = membersData.slice(0, limit);
      }

      // Update cache
      localStorage.setItem(cacheKey, JSON.stringify(membersData));
      localStorage.setItem(timestampKey, Date.now().toString());

      setData(membersData);
      setLoading(false);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching member data:', err);
      setError(err);
      setLoading(false);
    }
  }, [config, limit, cacheKey, timestampKey]);

  useEffect(() => {
    fetchData();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  return { data, loading, error, refetch, config };
};

export default useMemberData;